import { useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";

// The 17 UN Sustainable Development Goals, in their official colours.
export const SDGS = [
  [1, "No Poverty", "#E5243B"],
  [2, "Zero Hunger", "#DDA63A"],
  [3, "Good Health and Well-being", "#4C9F38"],
  [4, "Quality Education", "#C5192D"],
  [5, "Gender Equality", "#FF3A21"],
  [6, "Clean Water and Sanitation", "#26BDE2"],
  [7, "Affordable and Clean Energy", "#FCC30B"],
  [8, "Decent Work and Economic Growth", "#A21942"],
  [9, "Industry, Innovation and Infrastructure", "#FD6925"],
  [10, "Reduced Inequalities", "#DD1367"],
  [11, "Sustainable Cities and Communities", "#FD9D24"],
  [12, "Responsible Consumption and Production", "#BF8B2E"],
  [13, "Climate Action", "#3F7E44"],
  [14, "Life Below Water", "#0A97D9"],
  [15, "Life on Land", "#56C02B"],
  [16, "Peace, Justice and Strong Institutions", "#00689D"],
  [17, "Partnerships for the Goals", "#19486A"],
];

export const GRID_GOALS = [3, 4, 9, 10, 13];

// Official UN goal icons (public domain, via Wikimedia Commons). Shown unaltered.
export const sdgIcon = (number) => `/images/sdg/sdg-${String(number).padStart(2, "0")}.svg`;

export function SdgIcons({ goals = GRID_GOALS, className = "" }) {
  return (
    <span className={`sdg-icons ${className}`}>
      {goals.map((number) => (
        <img key={number} src={sdgIcon(number)} alt={`UN Sustainable Development Goal ${number}: ${SDGS[number - 1][1]}`} width="40" height="40" loading="lazy" decoding="async" />
      ))}
    </span>
  );
}

const EASE_OUT = [0.16, 1, 0.3, 1];
const STEP = 360 / 17;
const GAP = 1.6;

function polar(r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [100 + r * Math.cos(rad), 100 + r * Math.sin(rad)];
}

function segment(index, outer, inner) {
  const a0 = index * STEP + GAP / 2;
  const a1 = (index + 1) * STEP - GAP / 2;
  const [x0, y0] = polar(outer, a0);
  const [x1, y1] = polar(outer, a1);
  const [x2, y2] = polar(inner, a1);
  const [x3, y3] = polar(inner, a0);
  return `M${x0.toFixed(2)} ${y0.toFixed(2)} A${outer} ${outer} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)} L${x2.toFixed(2)} ${y2.toFixed(2)} A${inner} ${inner} 0 0 0 ${x3.toFixed(2)} ${y3.toFixed(2)} Z`;
}

// An SDG wheel drawn as SVG. GRID's goals keep their colour and push outward; the others fade to grey.
// `active` focuses one goal (used by the list beside the big wheel).
export function SdgWheel({ goals = GRID_GOALS, active = null, size = "full", delay = 0, className = "" }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const play = reduceMotion || inView;
  const mini = size === "mini";
  const outer = mini ? 96 : 92;
  const inner = mini ? 52 : 50;
  const focus = active ?? null;
  return (
    <svg ref={ref} className={`sdg-wheel sdg-wheel--${size} ${className}`} viewBox="0 0 200 200" role="img" aria-label={`UN Sustainable Development Goals wheel. GRID works toward goals ${goals.join(", ")}.`}>
      {SDGS.map(([number, name, colour], index) => {
        const ours = goals.includes(number);
        const dim = focus != null && focus !== number;
        const mid = (index + 0.5) * STEP;
        const push = ours && !mini ? 6 : 0;
        const [dx, dy] = [Math.cos(((mid - 90) * Math.PI) / 180) * push, Math.sin(((mid - 90) * Math.PI) / 180) * push];
        return (
          <motion.path
            key={number}
            d={segment(index, outer, inner)}
            fill={ours ? colour : "#e5e5e5"}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.6 }}
            animate={play
              ? { opacity: dim ? 0.25 : 1, scale: 1, x: focus === number ? dx * 1.8 : dx, y: focus === number ? dy * 1.8 : dy }
              : { opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.7, delay: play && !focus ? delay + index * 0.045 : 0, ease: EASE_OUT }}
            style={{ transformBox: "view-box", transformOrigin: "100px 100px" }}
          >
            <title>{`Goal ${number}: ${name}`}</title>
          </motion.path>
        );
      })}
      {!mini && (
        <g className="sdg-wheel__centre" aria-hidden="true">
          <circle cx="100" cy="100" r={inner - 6} fill="#ffffff" />
          <text x="100" y={focus ? 104 : 98} textAnchor="middle" className="sdg-wheel__big">{focus ?? goals.length}</text>
          <text x="100" y={focus ? 120 : 116} textAnchor="middle" className="sdg-wheel__small">{focus ? "Goal" : "of 17 goals"}</text>
        </g>
      )}
    </svg>
  );
}

// The landing-page section: the big wheel and the five goals, linked both ways on hover and focus.
export function SdgSection() {
  const [active, setActive] = useState(null);
  const reduceMotion = useReducedMotion();
  return (
    <section className="gh-sdg" aria-labelledby="gh-sdg-title">
      <div className="gh-sdg__wheel">
        <SdgWheel active={active} />
      </div>
      <div className="gh-sdg__copy">
        <h2 id="gh-sdg-title">Five UN goals we build for.</h2>
        <ol className="gh-sdg__list">
          {GRID_GOALS.map((number, index) => {
            const [, name] = SDGS[number - 1];
            return (
              <motion.li
                key={number}
                initial={reduceMotion ? false : { opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.7, delay: index * 0.07, ease: EASE_OUT }}
              >
                <button
                  type="button"
                  className={active === number ? "is-active" : undefined}
                  onMouseEnter={() => setActive(number)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(number)}
                  onBlur={() => setActive(null)}
                  onClick={() => setActive(active === number ? null : number)}
                >
                  <img className="gh-sdg__icon" src={sdgIcon(number)} alt="" width="72" height="72" loading="lazy" decoding="async" />
                  <span className="gh-sdg__text">
                    <strong>{name}</strong>
                  </span>
                </button>
              </motion.li>
            );
          })}
        </ol>
        <p className="gh-sdg__credit">Goal icons from the United Nations Sustainable Development Goals. The UN does not endorse Project GRID.</p>
      </div>
    </section>
  );
}
